import { z } from 'zod'
import source from '@/features/compliance/demo/anamnesebogenV2.json'

const readable = (value: string) =>
  [...value].every((c) => {
    const n = c.codePointAt(0)!
    return (n >= 32 && !(n >= 127 && n <= 159)) || [9, 10, 13].includes(n)
  })
const text = (max: number) =>
  z
    .string()
    .max(max)
    .refine((v) => !!v.trim() && readable(v), 'Bitte geben Sie einen gültigen Text ein.')
const id = z
  .string()
  .regex(/^[a-z][a-z0-9-]{0,79}$/)
  .refine((v) => !['constructor', 'prototype'].includes(v))
export const fieldKinds = ['text', 'yesNo', 'check', 'decimal', 'scale', 'date'] as const
const contentShape = {
  title: text(150).refine((v) => !/[\r\n\t]/.test(v)),
  exclusionHeading: text(1500),
  exclusionAttestation: text(1500),
  questions: z
    .array(z.strictObject({ id, label: text(500), required: z.literal(true) }))
    .min(1)
    .max(40),
  sections: z
    .array(
      z.strictObject({
        title: text(150),
        fields: z
          .array(
            z.strictObject({
              id,
              label: text(500),
              kind: z.enum(fieldKinds),
              required: z.boolean().optional(),
            }),
          )
          .min(1),
      }),
    )
    .min(1)
    .max(20),
  notices: z
    .array(z.strictObject({ title: text(150), text: text(6000) }))
    .min(1)
    .max(12),
}
export const catalogDefinitionSchema = z.strictObject(contentShape).superRefine((value, ctx) => {
  const fields = value.sections.flatMap((s) => s.fields)
  const ids = [...value.questions, ...fields].map((f) => f.id)
  if (fields.length > 100 || new Set(ids).size !== ids.length)
    ctx.addIssue({
      code: 'custom',
      message: 'Höchstens 100 Felder und eindeutige Fragekennungen sind erlaubt.',
    })
  if (new TextEncoder().encode(JSON.stringify(value)).length > 100000)
    ctx.addIssue({ code: 'custom', message: 'Die Vorlage ist zu umfangreich.' })
})
export type CatalogDefinition = z.infer<typeof catalogDefinitionSchema>

/** Compare retained definitions independently of JSON property order. */
export function catalogFingerprint(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(catalogFingerprint).join(',')}]`
  if (value && typeof value === 'object')
    return `{${Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${catalogFingerprint(v)}`)
      .join(',')}}`
  return JSON.stringify(value)
}
export function catalogContent(value: CatalogDefinition): CatalogDefinition {
  return catalogDefinitionSchema.parse({
    title: value.title,
    exclusionHeading: value.exclusionHeading,
    exclusionAttestation: value.exclusionAttestation,
    questions: value.questions,
    sections: value.sections,
    notices: value.notices,
  })
}
export const publishedCatalogSchema = z
  .strictObject({
    ...contentShape,
    formVersionId: z.string(),
    versionLabel: z.string(),
    locationId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    const content = catalogDefinitionSchema.safeParse({ ...catalogContentUnchecked(value) })
    if (!content.success)
      content.error.issues.forEach((issue) =>
        ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message }),
      )
    if (value.formVersionId === '2') {
      if (catalogFingerprint(value) !== catalogFingerprint(source))
        ctx.addIssue({ code: 'custom', message: 'Die ursprüngliche Vorlage wurde verändert.' })
    } else if (
      !z.uuid().safeParse(value.formVersionId).success ||
      !value.locationId ||
      value.versionLabel !== `Anamnesebogen · ${value.formVersionId}`
    )
      ctx.addIssue({ code: 'custom', message: 'Unbekannte Anamnesefassung.' })
  })
function catalogContentUnchecked(value: CatalogDefinition) {
  return {
    title: value.title,
    exclusionHeading: value.exclusionHeading,
    exclusionAttestation: value.exclusionAttestation,
    questions: value.questions,
    sections: value.sections,
    notices: value.notices,
  }
}
export type PublishedCatalog = z.infer<typeof publishedCatalogSchema>
export const DEFAULT_CATALOG = publishedCatalogSchema.parse(source)
const actor = z.strictObject({ id: z.string().min(1), name: text(200) })
export const draftSchema = z.strictObject({
  baseVersionId: z.string(),
  definition: catalogDefinitionSchema,
  savedAt: z.iso.datetime(),
  actor,
})
export const publicationSchema = z.strictObject({
  formVersionId: z.uuid(),
  publishedAt: z.iso.datetime(),
  actor,
})
export const catalogViewSchema = z
  .strictObject({
    locationId: z.string().min(1),
    revision: z.number().int().nonnegative(),
    active: publishedCatalogSchema,
    draft: draftSchema.nullable().optional(),
    publications: z.array(publicationSchema).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.active.locationId && value.active.locationId !== value.locationId) ||
      (value.draft && value.draft.baseVersionId !== value.active.formVersionId)
    )
      ctx.addIssue({ code: 'custom', message: 'Der Vorlagenkontext stimmt nicht überein.' })
  })
export type CatalogView = z.infer<typeof catalogViewSchema>
export const catalogAttestation = (catalog: CatalogDefinition) =>
  catalog.notices.map((n) => `${n.title}\n${n.text}`).join('\n\n')

export function catalogDataSchema(catalog: CatalogDefinition) {
  const fields = catalog.sections.flatMap((s) => s.fields)
  return z.record(z.string(), z.string().trim().max(1500)).superRefine((data, ctx) => {
    if (
      Object.keys(data).length !== fields.length ||
      fields.some((f) => !Object.hasOwn(data, f.id))
    )
      ctx.addIssue({
        code: 'custom',
        message: 'Die Anamnese ist unvollständig oder enthält unbekannte Felder.',
      })
    for (const f of fields) {
      const value = data[f.id]
      if (value === undefined) continue
      let valid = readable(value) && (!f.required || !!value)
      if (f.kind === 'yesNo') valid &&= ['', 'YES', 'NO'].includes(value)
      if (f.kind === 'check') valid &&= ['YES', 'NO'].includes(value)
      if (f.kind === 'decimal' && value) valid &&= /^\d{1,4}([.,]\d{1,2})?$/.test(value)
      if (f.kind === 'scale' && value) valid &&= /^(10|[1-9])$/.test(value)
      if (f.kind === 'date' && value)
        valid &&=
          /^\d{4}-\d{2}-\d{2}$/.test(value) &&
          !Number.isNaN(Date.parse(value)) &&
          new Date(value).toISOString().slice(0, 10) === value
      if (!valid)
        ctx.addIssue({
          code: 'custom',
          path: [f.id],
          message:
            f.required && !value
              ? 'Bitte beantworten Sie diese Frage.'
              : 'Bitte prüfen Sie diese Angabe.',
        })
    }
  })
}
export function emptyCatalogData(catalog: CatalogDefinition, name = ''): Record<string, string> {
  return Object.fromEntries(
    catalog.sections
      .flatMap((s) => s.fields)
      .map((f) => [
        f.id,
        f.kind === 'check' ? 'NO' : f.id === 'full-name' && f.kind === 'text' ? name : '',
      ]),
  )
}
